/* eslint-disable import/no-cycle */
import {
  columnHelper,
  getHeaderName
} from 'src/components/collapsible-table/table-column-prep';
import { ColumnDef } from '@tanstack/table-core';
import { Grid, Tooltip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import GetColumnForNumericType from './components/numeric-field-component';
import getColumnForPercentageType from './components/percentage-field-component';
import getHeaderForLegendFlag from './components/lagend-flag-component';
import getColumnForDTType from './components/dt-column-component';
import getColumnForActionSection from './components/action-column-component';
import getHeaderForCheckboxLabel from './components/checkbox-with-label-component';
import getHeaderForDateTimeFormat from './components/date-time-field-component';
import getHeaderForDateTimeHourFormat from './components/date-time-hour-field-component';
import getColumnForCheckboxType from './components/checkbox-field-component';
import getColumnForDropdownType from './components/dropdown-field-component';
import getColumnForTextType from './components/text-field-component';
import getColumnForDisplaySection from './components/sticky-field-column-component';
import DisplayCell from '../../../pricing-simulator/components/table-columns/display-cell';
import { formatNumberUsingRegex } from '../../../pricing-simulator';
import GetNoteColumn from './components/note-column-component';
import getDeleteColumn from './components/delete-column-component';
import {
  Header,
  InputType,
  TableComponent,
  TypeOfTableData
} from '../../../pricing-simulator/types/generic-table.model';
import getHeaderForOpportunityFlag from './components/opportunity-flag-component';
import getHeaderForCommentColumn from './components/comment-column.component';
import getColumnForPhRecomType from './components/ph-recommendation-column-component';
import getColumnForPriceThreshold from './components/price-threshold-column-component';
import generateColumnForImplementPeriod2Dropdown from './components/implement-period2-component';

/* 
    Getting Header for display section
*/
export const getDisplayColumn = (
  columnData: Header,
  footerRows: any[],
  index: number,
  componentType: string
): ColumnDef<{ [key: string]: number | string }> => {
  const subHeader: ColumnDef<{ [key: string]: number | string }> = {
    accessorKey: columnData.parent.parent_field,
    // id: columnData.parent.parent_field
    //   ? columnData.parent.parent_field
    //   : index.toString(),
    id: columnData.parent.parent_field
      ? `${columnData.parent.parent_field}-${columnData.parent.alias}-${index}` // Add alias to make the key more unique
      : `${index}`,
    cell: (info) =>
      columnData && columnData.parent.number_format
        ? formatNumberUsingRegex(columnData, +info.getValue())
        : info.getValue(),
    meta: {
      className:
        columnData.parent.field_format === InputType.TEXT
          ? 'last text-column'
          : 'last'
    },
    header: () => (
      <Grid container alignItems='center' flexDirection='column'>
        <Grid
          item
          xs='auto'
          container
          alignItems='center'
          className='change-text'
        >
          {columnData.children.length > 1 ? (
            <Grid item sx={{ cursor: 'pointer' }}>
              {columnData.parent.alias}
              {getHeaderName(columnData)}
            </Grid>
          ) : null}
        </Grid>
      </Grid>
    ),
    footer: () => {
      const data =
        footerRows &&
        footerRows.length &&
        TableComponent.PPGLEVELIMPACT === componentType
          ? footerRows[0].parent.data[columnData.parent.parent_field]
          : '';
      return (
        data && (
          <DisplayCell
            colorCode={columnData.parent.is_cf_enable}
            columnMetadata={columnData}
            value={data as number}
          />
        )
      );
    }
  };
  return subHeader;
};

/* 
    Getting Header based on the field format whose is_input_enable flag is true
*/
export const getColumnsForInputFieldFormat = (
  columnData: Header,
  columnMetadata: Header[],
  componentType: TableComponent,
  index: number,
  inputsDisabled: boolean,
  footerRows: any[],
  pricingAction1Value?: any,
  parentColData?: Header,
  maxElement?: number,
  isDisplayHeader?: boolean
) => {
  switch (columnData.parent.field_format) {
    case InputType.LEGEND_FLAG:
      return getHeaderForLegendFlag(columnData, componentType);

    case InputType.ACTION:
      return getColumnForActionSection(componentType, columnData);

    case InputType.CHECKBOX_LABEL:
      return getHeaderForCheckboxLabel(columnData, componentType);

    case InputType.DATETIME:
      return getHeaderForDateTimeFormat(columnData);

    case InputType.DATETIMEHOUR:
      return getHeaderForDateTimeHourFormat(columnData);

    case InputType.DECIMAL:
    case InputType.NUMBER:
      if (!columnData.parent.is_input_enable) {
        return getColumnForTextType(
          columnData,
          maxElement,
          isDisplayHeader,
          componentType,
          footerRows
        );
      }
      return GetColumnForNumericType(
        columnData,
        columnMetadata,
        componentType,
        parentColData,
        maxElement,
        inputsDisabled,
        isDisplayHeader
      );

    case InputType.PERCENTAGE:
      if (!columnData.parent.is_input_enable) {
        return getColumnForTextType(
          columnData,
          maxElement,
          isDisplayHeader,
          componentType,
          footerRows
        );
      }
      return getColumnForPercentageType(
        columnData,
        columnMetadata,
        componentType,
        maxElement,
        inputsDisabled,
        isDisplayHeader
      );

    case InputType.CHECKBOX:
      return getColumnForCheckboxType(
        columnData,
        columnMetadata,
        componentType,
        maxElement,
        inputsDisabled,
        isDisplayHeader
      );

    case InputType.SINGLESELECT_DROPDOWN:
      switch (columnData.parent.alias) {
        case 'Implement Period 2':
          return generateColumnForImplementPeriod2Dropdown(
            columnData,
            columnMetadata,
            pricingAction1Value,
            componentType,
            maxElement,
            inputsDisabled
          );
        default:
          return getColumnForDropdownType(
            columnData,
            columnMetadata,
            componentType,
            maxElement,
            inputsDisabled
          );
      }

    case InputType.TEXT:
      switch (columnData.parent.section) {
        case TypeOfTableData.DISPLAY:
          if (index === 0 && componentType !== 'SCENARIOMANAGER') {
            return getColumnForDisplaySection(
              componentType,
              columnData,
              columnMetadata,
              footerRows
            );
          }
          return getColumnForTextType(
            columnData,
            maxElement,
            isDisplayHeader,
            componentType
          );

        default:
          return getColumnForTextType(
            columnData,
            maxElement,
            isDisplayHeader,
            componentType
          );
      }

    case InputType.COMMENT_CARD:
      return getHeaderForCommentColumn(columnData);

    case InputType.NOTE:
      return GetNoteColumn(columnData, componentType);

    case InputType.DELETECOLUMN:
      return getDeleteColumn(columnData, componentType);

    default:
      return getColumnForTextType(columnData, maxElement, isDisplayHeader);
  }
};

export function flattenNestedColumns(data) {
  const flattenedColumns = [];

  function traverseAndFlatten(item, parentPath = []) {
    // If the current item has a parent object, process it
    if (item.parent) {
      const currentParent = item.parent;
      const newParentPath = [...parentPath, currentParent.alias || 'Unknown'];

      // If there are no children, this might be a leaf node to collect
      if (!item.children || item.children.length === 0) {
        flattenedColumns.push({
          ...currentParent,
          parentPath: newParentPath
        });
      }

      // Recursively process children if they exist
      if (item.children && item.children.length > 0) {
        item.children.forEach((child) => {
          traverseAndFlatten(child, newParentPath);
        });
      }
    }
  }

  // Start traversing from the top-level data
  data.forEach((item) => traverseAndFlatten(item));

  return flattenedColumns;
}

export function getDisplayTableColumns(columnData: any, visibleColumns?: any) {
  // Handle the 'User Input KPIs 1/2' case
  if (
    columnData.parent.alias.includes('User Input KPIs') &&
    columnData.children.length > 0
  ) {
    const allColsVisible = visibleColumns[columnData.parent.alias];
    const sortedChildren = columnData.children.sort(
      (a, b) => a.parent.order - b.parent.order
    );
    return allColsVisible ? sortedChildren : [sortedChildren[0]];
  }

  // Handle the 'KPIs - Current' or 'Key metrics - Current' case
  if (
    (columnData.parent.alias.includes('KPIs - Current') ||
      columnData.parent.alias.includes('Key metrics - Current')) &&
    columnData.children.length > 0
  ) {
    const allColsVisible = visibleColumns[columnData.parent.alias];
    const sortedChildren = columnData.children.sort(
      (a, b) => a.parent.order - b.parent.order
    );
    return allColsVisible ? sortedChildren : [sortedChildren[0]];
  }

  if (columnData.parent.alias.includes('Sim Results')) {
    return [columnData?.children];
  }

  if (columnData.parent.alias.includes('legendflag')) {
    return [columnData];
  }

  // Default return value if none of the above conditions matched
  if (columnData.children.length === 1) {
    const sortedChildren = columnData.children.sort(
      (a, b) => a.parent.order - b.parent.order
    );
    return [sortedChildren[0]];
  }

  return [columnData];
}

export function prepareExternalRowHeaders(columnData: any) {
  let dynamicData: any = {};

  if (
    columnData.parent.alias.includes('PPG') ||
    columnData.parent.alias.includes('legendflag') ||
    columnData.parent.alias.includes('DT') ||
    columnData.parent.alias.includes('BPE') ||
    columnData.parent.alias.includes('Threshold BPE') ||
    columnData.parent.alias.includes('Threshold BP') ||
    columnData.parent.alias.includes('Threshold (abs)') ||
    columnData.parent.alias === 'KPIs - Current' ||
    columnData.parent.alias === 'Key metrics - Current' ||
    columnData.parent.alias.includes('User Input KPIs') ||
    columnData.parent.alias.includes('Sim Results')
  ) {
    dynamicData = columnData.parent;
    return dynamicData;
  }
}
// TODO: create mapping for sim results - output columns - ext header to its child cols
export function createMappingKPI1(columnMetadata: Header[]) {
  const kpi1Column = columnMetadata.find(
    (data) => data.parent.alias === 'User Input KPIs 1'
  );

  if (!kpi1Column?.children?.length) return [];

  const kpi1ColumnMapping = kpi1Column.children.flatMap((child) => {
    if (!child.children || child.children.length === 0) {
      return [child.parent.parent_field];
    }

    return child.children
      .filter((grandChild) => grandChild.parent.is_visible)
      .map((grandChild) => grandChild.parent.parent_field);
  });

  // Include only columns whose parent_field contains 'cal_change_ip_perc'
  // and exclude implementation period columns
  return kpi1ColumnMapping.filter(
    (field) =>
      field !== 'pricing_implement_after_cal_1' &&
      field !== 'pricing_implement_after_cal_2' &&
      field.includes('cal_change_ip_perc')
  );
}

export function createMappingKPI2(columnMetadata: Header[]) {
  const kpi2Column = columnMetadata.find(
    (data) => data.parent.alias === 'User Input KPIs 2'
  );

  if (!kpi2Column?.children?.length) return [];

  const kpi2ColumnMapping = kpi2Column.children.flatMap((child) => {
    if (!child.children || child.children.length === 0) {
      return [child.parent.parent_field];
    }

    return child.children
      .filter((grandChild) => grandChild.parent.is_visible)
      .map((grandChild) => grandChild.parent.parent_field);
  });
  return kpi2ColumnMapping;
}

export function createMapping(columnMetadata: Header[]) {
  const mappingData: { [externalColumn: string]: string[] } = {};

  columnMetadata.forEach((columnData: Header) => {
    // Check if the alias matches any of the specific conditions
    if (
      columnData.parent.alias === 'PPG' ||
      columnData.parent.alias.includes('legendflag') ||
      columnData.parent.alias.includes('DT') ||
      columnData.parent.alias.includes('BPE') ||
      columnData.parent.alias.includes('Threshold BPE') ||
      columnData.parent.alias.includes('Threshold BP') ||
      columnData.parent.alias.includes('Threshold (abs)') ||
      columnData.parent.alias === 'KPIs - Current' ||
      columnData.parent.alias === 'Key metrics - Current' ||
      columnData.parent.alias.includes('User Input KPIs') ||
      columnData.parent.alias.includes('Sim Results')
    ) {
      // Map external column name (parent alias) to the list of tagged columns (children)
      mappingData[columnData.parent.alias] = columnData.children
        .map((child) => child.parent.is_visible && child.parent.alias)
        .filter((item) => item);
    }
  });
  return mappingData;
}

export function getColumnsForChildren(
  columnData: any,
  columnMetadata: any,
  componentType: TableComponent,
  inputsDisabled: boolean,
  pricingAction1Value: any
) {
  const dynamicChildrenData = [];
  const childrenMinOrder: number[] = columnData.children.map(
    (data) => data.parent.order
  );
  let maxElement = childrenMinOrder[0];
  for (let i = 1; i < childrenMinOrder.length; ++i) {
    if (childrenMinOrder[i] > maxElement) {
      maxElement = childrenMinOrder[i];
    }
  }
  const isDisplayHeader = columnData.children.length > 1;
  columnData.children
    .filter((col) => col.parent.is_visible)
    .slice()
    .sort((a, b) => a.parent.order - b.parent.order)
    .forEach((colData) => {
      dynamicChildrenData.push(
        getColumnsForInputFieldFormat(
          colData,
          columnMetadata,
          componentType,
          undefined,
          inputsDisabled,
          null,
          pricingAction1Value,
          columnData,
          maxElement,
          isDisplayHeader
        )
      );
    });
  return dynamicChildrenData;
}

export function getExternalRowData(columnMetadata: Header[]) {
  // Create a shallow copy of columnMetadata to avoid mutating the original array
  const sortedColumnMetadata = [...columnMetadata].sort(
    (a, b) => a.parent.order - b.parent.order
  );

  const colData = [];
  sortedColumnMetadata.forEach((columnData: Header, index: number) => {
    const extData = prepareExternalRowHeaders(columnData);
    extData && colData.push(extData);
  });

  console.log('colData', colData);
  return colData;
}

//const labelstoExclude = ['PPG', {/*'DT'*/}, 'BPE', 'Threshold BPE', {/*'Retailer'*/}, 'BP', 'Threshold BP']

/*
 ** Creating the columns of the table
 ** based on the ${InputType}
 */
export default function getTableColumns(
  columnMetadata: Header[],
  columnHeaders: Header[],
  componentType: TableComponent,
  footerRows: any[] = [],
  inputsDisabled = false,
  moduleId?: number,
  sourceId?: number,
  scenarioId?: number,
  visibleColumns?: any,
  pricingAction1Value?: any,
  activeHeader?: any
) {
  const allColumnDefs = [];

  let labelstoExclude = ['BPE', 'Threshold BPE', 'BP', 'Threshold BP'];
  if (
    componentType !== TableComponent.INNOVATION &&
    componentType !== TableComponent.PLANNER
  ) {
    labelstoExclude.unshift('PPG');
  }

  // Iterate over each column metadata
  columnMetadata?.forEach((columnData?: any, index?: number) => {
    let filter = true;
    if (componentType === TableComponent.PRICING) {
      if (
        columnData.parent.alias != 'Retailer' &&
        columnData.parent.alias != 'Channel' &&
        columnData.parent.is_visible &&
        columnData.children.filter((col: any) => col.parent.is_visible)
      ) {
        filter = true;
      } else {
        filter = false;
      }
    }

    if (filter) {
      const displayTableColumns = getDisplayTableColumns(
        columnData,
        visibleColumns
      );

      // Create separate subHeaders for this specific columnData
      displayTableColumns?.forEach((data: any) => {
        const subHeaders = []; // Initialize subHeaders for each displayTableColumn

        // Ensure data.parent exists before accessing its properties
        if (!data.parent) {
          console.warn('Missing parent data for:', data);
          return; // Skip this iteration if parent is missing
        }

        // Add appropriate sub-header based on parent properties
        if (data.parent.section === TypeOfTableData.OPPORTUNITY_FLAG) {
          subHeaders.push(getHeaderForOpportunityFlag(data));
        } else if (
          data.parent.field_format === InputType.DT_PBI_IFRAME_TYPE_1
        ) {
          subHeaders.push(
            getColumnForDTType(data, moduleId, sourceId, scenarioId)
          );
        } else if (
          data.parent.field_format === InputType.PH_RECOMMENDATION_TYPE
        ) {
          subHeaders.push(
            getColumnForPhRecomType(data, moduleId, sourceId, scenarioId)
          );
        } else if (
          columnData.parent.field_format === InputType.PRICE_THRESHOLD_TYPE
        ) {
          subHeaders.push(getColumnForPriceThreshold(columnData));
        } else if (data.parent.field_format === InputType.LEGEND_FLAG) {
          subHeaders.push(getHeaderForLegendFlag(data, componentType));
        } else if (data.parent.field_format === InputType.ACTION) {
          subHeaders.push(getColumnForActionSection(componentType, data));
        } else if (data.children && data.children.length > 0) {
          subHeaders.push(
            ...getColumnsForChildren(
              data,
              columnHeaders,
              componentType,
              inputsDisabled,
              pricingAction1Value
            )
          );
        } else if (data.parent.field_format === InputType.NOTE) {
          subHeaders.push(GetNoteColumn(data, componentType));
        } else if (data.parent.field_format === InputType.DELETECOLUMN) {
          subHeaders.push(getDeleteColumn(data, componentType));
        } else {
          subHeaders.push(
            getColumnsForInputFieldFormat(
              data,
              columnHeaders,
              componentType,
              index,
              inputsDisabled,
              footerRows,
              pricingAction1Value
            )
          );
        }

        // // Create column definitions for this displayTableColumn
        // const id = data.parent.parent_field
        //   ? data.parent.parent_field
        //   : index.toString();

        const id = data.parent.parent_field
          ? `${data.parent.parent_field}-${data.parent.alias}-${index}` // Add alias to make the key more unique
          : `${index}`;

        const columnDef = columnHelper.group({
          id,
          header: (props) =>
            data.parent.section !== TypeOfTableData.LEGEND_FLAG ? (
              <span
                className={
                  !labelstoExclude.includes(data.parent.alias)
                    ? 'normal-headers'
                    : 'hidden-headers'
                }
              >
                {data.parent.alias}
                {data.parent.is_info_enable ? (
                  <Tooltip title={data.parent.info_details}>
                    <InfoOutlinedIcon
                      style={{
                        fontSize: '14px',
                        paddingTop: '0px',
                        marginLeft: '6px'
                      }}
                    />
                  </Tooltip>
                ) : null}
              </span>
            ) : (
              <span className='legend-col' />
            ),
          meta: {
            className:
              data.parent.section === TypeOfTableData.LEGEND_FLAG
                ? 'legend-col'
                : !data.parent.has_border
                  ? 'hide-border-col'
                  : ''
          },
          columns: subHeaders // Pass the specific subHeaders for this column definition
        });
        // console.log('subHeaders', subHeaders)
        // Add the current columnDef to the flattened array
        allColumnDefs.push(columnDef);
      });
    }
  });

  return allColumnDefs; // Return the flattened array of all column definitions
}
